import {
	Vue, Component, Ref, toNative,
} from 'vue-facing-decorator';
import Swal from 'sweetalert2';
import { BModal } from 'bootstrap-vue';
import { CellComponentExtended, ColumnDefinitionExtended, TabulatorFull as Tabulator } from 'tabulator-tables';
import mitt from 'mitt';
import { TabEvent } from 'interfaces/app';
import TabulatorBtn from 'components/Tabulator/TabulatorBtn';
import { httpClient } from 'utils/http';
import * as DB from 'interfaces/database';
import { createInstance } from 'utils/vue';
import BadgeService from 'services/BadgeService';
import Template from './template.vue';

const eventBus = mitt<TabEvent<DB.BadgeModel>>();
@Component({
	components: {
		TabulatorBtn,
	},
	emits: ['routeBadge'],
	mixins: [Template],
})
class BadgesTable extends Vue {
	@Ref('my-modal')
	private readonly modal!: BModal;

	@Ref('table')
	private readonly tableReference!: HTMLDivElement;

	private name = '';

	private isLoaded = false;

	private table?: Tabulator;

	private perPage = 20;

	private columnDefs: ColumnDefinitionExtended[] = [];

	private isLoading = false;

	private pageOptions = [5, 10, 15, 20, 25, 30, 35, 40, 45, 50];

	private get loggedIn(): boolean {
		return this.$auth0.isAuthenticated.value;
	}

	private beforeMount(): void {
		this.columnDefs = [
			{
				field: 'id',
				title: 'ID',
			},
			{
				title: 'Name',
				field: 'name',
			},
			{
				title: 'Actions',
				formatter: (cell: CellComponentExtended<DB.BadgeModel>) => {
					const instance = createInstance({
						component: TabulatorBtn,
						props: {
							data: cell.getData(),
							buttons: [
								{
									id: 'edit',
									eventName: 'editItem',
									className: 'fa-edit',
								},
								{
									id: 'delete',
									eventName: 'deleteItem',
									className: 'fa-times',
								},
							],
							eventBus,
						},
					});
					return instance;
				},
			},
		] as ColumnDefinitionExtended[];
	}

	private mounted(): void {
		eventBus.on(
			'deleteItem',
			this.deleteBadge,
		);
		eventBus.on(
			'editItem',
			this.editBadge,
		);
		this.table = new Tabulator(
			this.tableReference,
			{
				layout: 'fitColumns',
				height: '60vh',
				columns: this.columnDefs,
				ajaxURL: '/api/badge',
				ajaxParams: {
					limit: this.perPage,
				},
				pagination: true,
				paginationSize: 20,
				paginationSizeSelector: [5, 10, 15, 20, 25, 30, 35, 40, 45, 50],
				paginationMode: 'remote',
				ajaxRequestFunc: (url, _config, params) => {
					const { page, size } = params;
					return httpClient.get(`${url}?limit=${size}&offset=${(page - 1) * size}`);
				},
				ajaxResponse: (_url, _params, response) => {
					const last_page = response.headers['x-total-records'];
					return {
						data: response.data,
						last_page,
					};
				},
			},
		);
	}

	private beforeUnmount(): void {
		eventBus.off(
			'deleteItem',
			this.deleteBadge,
		);
		eventBus.off(
			'editItem',
			this.editBadge,
		);
		this.table?.destroy();
	}

	protected addBadge(): void {
		// add a badge
		this.isLoaded = true;
		BadgeService.create(
			{ name: this.name },
		).then((data) => {
			this.modal.hide();

			this.table?.addData(
				[{
					id: data.id,
					name: data.name,
				}],
				true,
			);
			this.name = '';
			this.$toastSuccess('Badge added successfully');
			this.$emit(
				'routeBadge',
				data.id,
			);
			return undefined;
		})
			.finally(() => {
				this.isLoaded = false;
			})
			.catch((err) => {
				this.$toastError(err.message);
			});
	}

	private editBadge(data: TabEvent<DB.BadgeModel>['editItem']): void {
		this.$emit(
			'routeBadge',
			data.id,
		);
	}

	private deleteBadge(data: TabEvent<DB.BadgeModel>['deleteItem']): void {
		Swal.fire({
			title: 'Are you sure?',
			text: 'You will not be able to recover this file!',
			icon: 'warning',
			showCancelButton: true,
			customClass: {
				confirmButton: 'btn btn-danger m-1',
				cancelButton: 'btn btn-secondary m-1',
			},
			confirmButtonText: 'Yes, delete it!',
			html: false,
		}).then((result) => {
			if (result.value) {
				// eslint-disable-next-line promise/no-nesting
				BadgeService.delete(data.id).then(() => {
					this.$toastSuccess('Item Deleted');
					this.table?.replaceData();
					return null;
				}).catch((err) => {
					this.$toastError(err.message);
				});
			}
			return undefined;
		}).catch((err) => {
			this.$toastError(err.message);
		});
	}
}

export default toNative(BadgesTable);
